import { FEATURE_FLAGS } from '@highlight/analytics'
import { useFlags } from '@highlight/analytics/react'
import { useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'
import GlobalLogger from '@shared/utils/logger/GlobalLogger'
import { getChatApiRoute } from '../api/v1Client'
import { describeCause } from '../engine/turn-trace'
import { useAuth } from '../providers'
import { useGetRealChatId } from '../store/chats'
import { useLocalChatIds } from '../store/local-chat-ids'
import { useIsTurnLive } from '../store/turn-status'
import { createTurnPreparation, type RequestTurnPreparation, type TurnPreparationReply } from './turn-preparation'
import { useIsAgenticChatEnabled } from './use-agentic-chat'
import { chatModeFields } from './use-chat-api'

/** A prepare call that takes longer than this has missed the send it was for. */
const PREPARE_TIMEOUT_MS = 5_000

const log = GlobalLogger.bind('ChatPrepare')

// One per window, so the composer and the submit flow see the same pending new chat.
const turnPreparation = createTurnPreparation({ mintConversationId: uuidv4, now: Date.now })

/**
 * Live, because the composer stays mounted for the life of the window and a sticky read would only follow the
 * flag after a restart. Unexposed, because the flag changes how soon a turn starts, not what the user sees.
 */
function useIsTurnPreparationEnabled(): boolean {
  return useFlags({ live: true }).isEnabled(FEATURE_FLAGS.CHAT_PREPARE_TURN, false, { expose: false })
}

function useRequestTurnPreparation(): RequestTurnPreparation {
  const { getAuthToken } = useAuth()
  const isAgenticChatEnabled = useIsAgenticChatEnabled()

  return useCallback<RequestTurnPreparation>(
    async (target) => {
      try {
        const response = await fetch(getChatApiRoute('api/v1/chat/prepare'), {
          method: 'POST',
          body: JSON.stringify({ ...target, ...chatModeFields(isAgenticChatEnabled) }),
          headers: { Authorization: `Bearer ${await getAuthToken()}`, 'Content-Type': 'application/json' },
          signal: AbortSignal.timeout(PREPARE_TIMEOUT_MS),
        })
        if (!response.ok) {
          log.info(`prepare answered ${response.status}`)
          return null
        }
        return (await response.json()) as TurnPreparationReply
      } catch (error) {
        // The send that follows needs no preparation, so a failure here costs the user nothing.
        log.warn(`prepare failed: ${describeCause(error)}`)
        return null
      }
    },
    [getAuthToken, isAgenticChatEnabled],
  )
}

/**
 * Returns a function that asks the backend to prepare the next message to
 * `chatId`, or to a new chat when there is none. It does nothing while the
 * flag is off, while the chat has a turn running, or for a chat whose id the
 * backend has not confirmed yet.
 */
export function usePrepareTurn(chatId: string | undefined): () => void {
  const isEnabled = useIsTurnPreparationEnabled()
  const requestTurnPreparation = useRequestTurnPreparation()
  const getRealChatId = useGetRealChatId()
  const localChatIds = useLocalChatIds()
  const isTurnLive = useIsTurnLive(chatId)
  const conversationId = chatId === undefined ? undefined : getRealChatId(chatId)
  const isUnconfirmedId = conversationId !== undefined && localChatIds.has(conversationId)

  return useCallback(() => {
    if (!isEnabled || isTurnLive || isUnconfirmedId) {
      return
    }
    turnPreparation.prepare(conversationId, requestTurnPreparation)
  }, [isEnabled, isTurnLive, isUnconfirmedId, conversationId, requestTurnPreparation])
}

/** Hands the submit flow the id a new chat was prepared under. Always undefined while the flag is off. */
export function useTakePreparedConversationId(): () => string | undefined {
  const isEnabled = useIsTurnPreparationEnabled()
  return useCallback(() => (isEnabled ? turnPreparation.takeNewChatId() : undefined), [isEnabled])
}
